import type { Question } from '../../config/types';
import type { AthenaLookupRow, FetchFormResponse, UpdateFormParams } from '../../service/types';
import { hasValue } from '../form-utils';
import { getListQuestionDescriptors } from '../list-question-utils';
import { getListPageExcelColumns, getListPageExcelColumnTitle } from '../list-page-excel/columns';
import {
  buildListPageReplacementAnswers,
  getListPageEditorRows,
  validateListPageEditorRows,
} from '../list-page-excel/editor-model';
import { validateListPageExcelRows } from '../list-page-excel/validation';
import type { CdrSection } from './workbook';

export type CdrChoice = { selected: boolean; mode: 'overwrite' | 'append' | 'keep' };
export type CdrImportSection = {
  section: CdrSection;
  headings: string[];
  rows: unknown[][];
  errors: string[];
  updates: UpdateFormParams[];
  mode: CdrChoice['mode'];
};

const normalise = (value: unknown) =>
  String(value ?? '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase();
const organisationName = (value: unknown) =>
  normalise(value)
    .replace(/\s*\([^)]*\)$/, '')
    .replace(/&/g, 'and');
export const hasCdrData = (section: CdrSection) =>
  section.rows.length > 0 || Object.values(section.values).some(hasValue);
const optionsFor = (section: CdrSection, answers: Record<string, unknown>) => ({
  title: section.title,
  pageName: section.page.name,
  questions: section.page.questions || [],
  tenant: 'psfa',
  answers,
});
export const hasExistingCdrData = (section: CdrSection, answers: Record<string, unknown>) =>
  hasValue(answers[section.declaration?.questions?.[0]?.name || '']) ||
  (section.page.pageType === 'list'
    ? getListPageEditorRows(optionsFor(section, answers)).length > 0
    : (section.page.questions || []).some(({ name }) => hasValue(answers[name])));

export const buildCdrImport = (
  sections: CdrSection[],
  choices: Record<string, CdrChoice>,
  current: Pick<FetchFormResponse, 'answers' | 'drafts'>,
  organisations: AthenaLookupRow[] = [],
): CdrImportSection[] =>
  sections
    .filter((section) => choices[section.id]?.selected && hasCdrData(section))
    .map((section) => {
      const mode = choices[section.id].mode;
      const options = optionsFor(section, current.answers);
      const questions = options.questions;
      if (mode === 'keep') {
        const visible = questions.filter((question) => question.questionType !== 'hidden');
        return {
          section,
          headings: visible.map((question) => question.title),
          rows: [
            visible.map((question) => {
              const value = current.answers[question.name];
              return value && typeof value === 'object' ? Object.values(value).join(' ') : value;
            }),
          ],
          errors: [],
          updates: [],
          mode,
        };
      }
      const errors: string[] = [...(section.sourceErrors || [])];
      let rows: unknown[][];
      let headings: string[];
      let answers: Record<string, unknown>;
      if (section.page.pageType === 'list') {
        rows = [...(mode === 'append' ? getListPageEditorRows(options) : []), ...section.rows];
        const columns = getListPageExcelColumns(getListQuestionDescriptors(questions));
        const validation = validateListPageEditorRows(options, rows);
        const comments = columns.findIndex((column) =>
          column.descriptors[0].question.name.endsWith('_comments'),
        );
        validation.rows.forEach((row, rowIndex) => {
          const unmatched = columns.flatMap((column, columnIndex) =>
            hasValue(rows[rowIndex][columnIndex]) &&
            column.descriptors.every(({ question }) => !(question.name in row.answers))
              ? [`${getListPageExcelColumnTitle(column)}: ${rows[rowIndex][columnIndex]}`]
              : [],
          );
          if (unmatched.length && comments >= 0) {
            const name = columns[comments].descriptors[0].question.name;
            row.answers[name] = [row.answers[name], ...unmatched].filter(hasValue).join('\n');
            rows[rowIndex] = rows[rowIndex].map((value, columnIndex) =>
              columnIndex === comments ? row.answers[name] : value,
            );
          }
        });
        errors.push(
          ...validation.errors.map(
            (error) => `Row ${error.worksheetRow}, ${error.fieldTitle}: ${error.message}`,
          ),
        );
        answers = buildListPageReplacementAnswers(options, validation.rows);
        headings = columns.map(getListPageExcelColumnTitle);
      } else {
        answers = Object.fromEntries(
          questions.map((question) => [question.name, section.values[question.name] ?? '']),
        );
        const validationQuestions = questions
          .filter((question) => question.questionType !== 'hidden')
          .map((question): Question => {
            if (question.name === 'report-period')
              return { ...question, questionType: 'textInput', rules: { required: true } };
            if (question.name !== 'department' && question.name !== 'sub_organisation')
              return question;
            const field = question.name === 'department' ? 'dept_name' : 'org_name';
            const available =
              question.name === 'department'
                ? organisations
                : organisations.filter((row) => row.dept_name === answers.department);
            const matches = [
              ...new Set(
                available.map((row) => row[field]).filter((value): value is string => !!value),
              ),
            ];
            const matched = matches.filter(
              (value) => organisationName(value) === organisationName(answers[question.name]),
            );
            if (matched.length === 1) answers[question.name] = matched[0];
            const selected = available.find((row) => row[field] === answers[question.name]);
            answers[question.name === 'department' ? 'department_id' : 'organisation_id'] =
              selected?.[question.name === 'department' ? 'dept_id' : 'org_id'] ?? '';
            return { ...question, options: matches };
          });
        const descriptors = getListQuestionDescriptors(validationQuestions);
        const columns = getListPageExcelColumns(descriptors);
        const period = answers['report-period'];
        const scalarAnswers = { ...answers };
        if (section.id === 'department-select') {
          const value = period as { quarter?: string; year?: string } | undefined;
          const valid =
            value &&
            typeof value === 'object' &&
            /^Quarter [1-4] \(.+\)$/.test(value.quarter || '') &&
            /^\d{4}-\d{2}$/.test(value.year || '');
          if (!valid) errors.push('Period of Report: select a valid reporting quarter and year.');
          scalarAnswers['report-period'] =
            typeof period === 'object'
              ? `${value?.quarter || ''} ${value?.year || ''}`.trim()
              : period;
        }
        rows = [columns.map((column) => scalarAnswers[column.descriptors[0].question.name] ?? '')];
        const validation = validateListPageExcelRows({
          rows: [{ worksheetRow: 1, values: rows[0] }],
          descriptors,
          tenant: 'psfa',
        });
        errors.push(...validation.errors.map((error) => `${error.fieldTitle}: ${error.message}`));
        Object.assign(answers, validation.rows[0].answers);
        if (section.id === 'department-select') answers['report-period'] = period;
        headings = columns.map(getListPageExcelColumnTitle);
        rows = [
          columns.map((column) =>
            column.descriptors[0].question.name === 'report-period'
              ? scalarAnswers['report-period']
              : answers[column.descriptors[0].question.name],
          ),
        ];
      }
      const update = (
        pageName: string,
        values: Record<string, unknown>,
        draft: boolean,
      ): UpdateFormParams => ({
        'subsection-id': section.id,
        'page-id': pageName,
        'is-save-unfinished': draft,
        answers: values,
      });
      const updates: UpdateFormParams[] = [];
      if (section.declaration?.questions?.[0])
        updates.push(
          update(
            section.declaration.name,
            { [section.declaration.questions[0].name]: 'Yes' },
            false,
          ),
        );
      updates.push(update(section.page.name, answers, errors.length > 0));
      for (const draft of current.drafts?.[section.id] || []) {
        if (section.page.pageType === 'list' && draft.startsWith(`${section.page.name}:`))
          updates.push({
            ...update(section.page.name, { [section.page.name]: [] }, false),
            'list-item-id': draft.slice(section.page.name.length + 1),
          });
      }
      return { section, headings, rows, errors, updates, mode };
    });

export const saveCdrImport = async (
  plan: CdrImportSection[],
  save: (update: UpdateFormParams) => Promise<void>,
  onSaved: (section: CdrImportSection) => void,
) => {
  for (const item of plan) {
    if (item.updates.length === 0) continue;
    for (const update of item.updates) await save(update);
    onSaved(item);
  }
};
